import { Route, Routes } from "react-router";

import { LoginPage } from "../login/LoginPage.tsx";
import { ForgotPassword } from "../passwordReset/ForgotPassword.tsx";
import { ResetPassword } from "../passwordReset/ResetPassword.tsx";
import { ProofCheck } from "../proof/ProofCheck.tsx";
import { ViewingRoom } from "../room/ViewingRoom.tsx";
import { Signup } from "../signup/Signup.tsx";
import { WorkspaceRoute } from "../workspace/WorkspaceRoute.tsx";
import { WorkspacesPage } from "../workspace/WorkspacesPage.tsx";
import { NotFound } from "./NotFound.tsx";
import { LandingRedirect, RequireSession, SignedOutOnly } from "./gates.tsx";
import { PATHS } from "./routes.constants.ts";

/**
 * Every address in ONDO, in one table.
 *
 * Screens are addressable; steps inside a form are not. The signup and
 * registration wizards each sit behind a single URL and keep their stage
 * unions, because a step is not a place you can arrive at cold — see
 * `useCollectorController` for the whole argument.
 *
 * A record DOES have a URL, and exactly one destination takes one: a work.
 * `useCollectorController` says what that address is for and what must never be
 * built on it.
 *
 * The two workspace patterns render the same element on purpose: `/:role/:org`
 * resolves to the workspace's default destination and redirects there, so what
 * is on screen and what is in the address bar can never disagree.
 *
 * Two addresses sit outside both gates and ask nobody who they are:
 * `/room/:handle/:slug`, the first public surface in ONDO, and `/proof`, which
 * reads nothing from ONDO at all. The paragraphs beside each say what makes
 * that safe.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path={PATHS.root} element={<LandingRedirect />} />

      <Route element={<SignedOutOnly />}>
        <Route path={PATHS.signIn} element={<LoginPage />} />
        <Route path={PATHS.apply} element={<Signup />} />
        <Route path={PATHS.forgotPassword} element={<ForgotPassword />} />
      </Route>

      {/* THE SECOND ADDRESS OUTSIDE BOTH GATES, and the reason has the same shape as the
          viewing room's: the two gates would have to disagree about one URL.

          A Supabase recovery link MAKES a session — the client reads the tokens out of
          the fragment on load — so `SignedOutOnly` would bounce every visitor who
          arrived correctly. And `RequireSession` would bounce anybody whose link had
          expired, sending them to sign in with the password they came here because they
          do not know.

          What makes it safe is not this table. The screen holds no authority and reads
          no token: by the time it renders, the library has either established an
          ordinary session or it has not, and `updateUser({ password })` is refused
          without one by Supabase rather than by us. See `useResetPasswordController`,
          which also records why — unlike the implementation this replaced — it does NOT
          sign out a session it finds. */}
      <Route path={PATHS.resetPassword} element={<ResetPassword />} />

      <Route element={<RequireSession />}>
        <Route path={PATHS.workspaces} element={<WorkspacesPage />} />
        <Route path=":role/:org" element={<WorkspaceRoute />} />
        <Route path=":role/:org/:destination" element={<WorkspaceRoute />} />
        <Route path=":role/:org/:destination/:record" element={<WorkspaceRoute />} />
      </Route>

      {/* The first address in ONDO that did not ask who is reading it, and
          for a year the only route outside both gates. RequireSession would bounce a visitor
          with no account — which is most of the people this page exists for —
          and SignedOutOnly would bounce a member following the same link, so
          the two would have to disagree about the same URL.

          What makes that safe is not this table. `ondo.viewing_room_contents()`
          returns a row that is already redacted, and the screen behind this
          route filters nothing: it renders what its owner chose to publish. See
          the header of 20260805090000_viewing_rooms.sql. */}
      <Route path={PATHS.room} element={<ViewingRoom />} />

      {/* The proof page — OND-53's bundle, checked. Outside both gates for the
          room's reason, and safe for a plainer one: it makes no request. The
          whole page is copy plus what the visitor's own file said once their
          own machine had hashed it, and `PATHS.proof` says why that must not
          need an account. */}
      <Route path={PATHS.proof} element={<ProofCheck />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
